import axios from 'axios';
import API from '../config/api';
import { auth } from '../firebaseConfig';
import { canSyncDoctor, profileOfflineEnabled } from './config';
import { localStore } from './localStore';
import { activeOfflineDoctor } from './access';

export function cacheOwner() {
  const doctor = activeOfflineDoctor();
  if (!profileOfflineEnabled || !auth.currentUser || !canSyncDoctor(doctor)) return '';
  return JSON.stringify([auth.currentUser.uid, doctor]);
}
export function readKey(url, params = {}) {
  const endpoint = new URL(url, API.BASE_URL);
  if (endpoint.origin !== new URL(API.BASE_URL).origin) return null;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) endpoint.searchParams.set(key, String(value));
  });
  endpoint.searchParams.delete('bypassCache');
  endpoint.searchParams.sort();
  return endpoint.pathname + '?' + endpoint.searchParams.toString();
}
const emit = detail => window.dispatchEvent(new CustomEvent('offlineReadStatus', { detail }));
export async function profileGet(url, config = {}) {
  const owner = cacheOwner();
  const requestKey = readKey(url, config.params);
  if (!owner || !requestKey) return axios.get(url, config);
  const key = JSON.stringify([owner, requestKey]);
  if (navigator.onLine) {
    try {
      const response = await axios.get(url, { timeout: 15000, ...config });
      if (cacheOwner() !== owner) throw Object.assign(new Error('Account changed.'), { code: 'SESSION_CHANGED' });
      try {
        await localStore.put('responses', { key, owner, savedAt: Date.now(), data: response.data });
      } catch { emit({ storageError: true }); }
      return response;
    } catch (error) {
      // Never hide permission failures, validation errors or deliberate cancellation.
      if (error.response || axios.isCancel(error) || error.code === 'SESSION_CHANGED') throw error;
    }
  }
  const saved = await localStore.get('responses', key);
  if (cacheOwner() !== owner) throw new Error('Account changed.');
  if (!saved) {
    const draft = await findDraftPatient(owner, requestKey);
    if (draft) {
      const pathname = new URL(url, API.BASE_URL).pathname;
      const empty = pathname === '/api/patients/visits'
        ? { patient_info: { name: draft.patient_name, phone: draft.patient_phone, age: draft.age || '', address: draft.address || '' },
            pagination: { currentPage: 1, totalPages: 0, totalVisits: 0 }, visits: [], complaint_history: [], voice_notes: [] }
        : pathname.includes('/medical-history/patient')
          ? { success: true, data: {}, template_snapshot: null }
          : { success: true, data: [], patients: [], reports: [], packages: [], responses: [] };
      emit({ savedAt: draft._offlineCreatedAt || Date.now() });
      return { data: empty, status: 200, statusText: 'New offline patient', headers: {}, config, offline: true };
    }
    emit({ missing: true });
    throw Object.assign(new Error('This section has not been downloaded on this device. Connect to download it.'), { code: 'OFFLINE_CACHE_MISS' });
  }
  emit({ savedAt: saved.savedAt });
  return { data: saved.data, status: 200, statusText: 'Saved device copy', headers: {}, config, offline: true, savedAt: saved.savedAt };
}

async function findDraftPatient(owner, requestKey) {
  const values = decodeURIComponent(requestKey);
  if (!values.includes('local_') && !values.includes('offline_')) return null;
  const rows = await localStore.list('patients');
  return rows.find(row => row.owner === owner && row.patient?._offlineDraft &&
    [row.patient._id, row.patient.patient_id].some(id => id && values.includes(id)))?.patient || null;
}

export async function rememberPatients(patients, scope) {
  const owner = cacheOwner();
  if (!owner) return;
  for (const patient of patients) {
    if (!patient._id) continue;
    const key = JSON.stringify([owner, scope, patient._id]);
    const previous = await localStore.get('patients', key);
    if (cacheOwner() !== owner) return;
    await localStore.put('patients', { key, owner, scope, patient: { ...previous?.patient, ...patient }, savedAt: Date.now() });
  }
}
export async function cachedPatients(scope, search = '') {
  const owner = cacheOwner();
  if (!owner) return [];
  const rows = await localStore.list('patients');
  // Older dashboard builds cached a newly-added offline patient immediately.
  // Keep those stale drafts out of the patient list while their waiting action
  // has not been entered by the doctor yet.
  const waitingOnlyIds = new Set((await localStore.list('waiting'))
    .filter(row => row.owner === owner && row.createPatient && !row.entered)
    .flatMap(row => [row.localPatientId, row.patient?._id, row.patient?.patient_id])
    .filter(Boolean)
    .map(String));
  if (cacheOwner() !== owner) return [];
  const term = search.trim().toLowerCase();
  return rows.filter(row => row.owner === owner && row.scope === scope &&
    ![row.patient?._id, row.patient?.patient_id].some(id => id && waitingOnlyIds.has(String(id))) &&
    [row.patient.patient_name, row.patient.patient_phone, row.patient.file_number, row.patient.patient_id]
      .some(value => String(value || '').toLowerCase().includes(term)))
    .map(row => row.patient);
}

export async function removeWaitingOnlyPatient(patient, scope) {
  const owner = cacheOwner();
  if (!owner) return;
  const ids = new Set([patient?._id, patient?.patient_id].filter(Boolean).map(String));
  if (!ids.size) return;
  const rows = await localStore.list('patients');
  for (const row of rows) {
    if (row.owner !== owner || (scope && row.scope !== scope)) continue;
    if ([row.patient?._id, row.patient?.patient_id].some(id => id && ids.has(String(id)))) {
      await localStore.remove('patients', row.key);
    }
  }
  if (cacheOwner() === owner) {
    window.dispatchEvent(new CustomEvent('offlinePatientWaitingOnly', { detail: { ids: [...ids] } }));
  }
}

export async function replaceDraftPatient(localId, patient, revision) {
  const owner = cacheOwner();
  if (!owner || !localId || !patient?._id) return;
  const rows = (await localStore.list('patients')).filter(row => row.owner === owner && row.patient?._id === localId);
  for (const row of rows) {
    const replacement = { ...row.patient, ...patient, _offlineDraft: false, offlinePending: false };
    await localStore.put('patients', { ...row, key: JSON.stringify([owner, row.scope, patient._id]), patient: replacement, savedAt: Date.now() });
    await localStore.remove('patients', row.key);
  }
  await localStore.put('revisions', { key: JSON.stringify([owner, localId]), revision, serverPatientMongoId: patient._id, patient });
  await localStore.put('revisions', { key: JSON.stringify([owner, patient._id]), revision, patient });
  window.dispatchEvent(new CustomEvent('offlinePatientSynchronized', { detail: { localId, patient } }));
}
